/**
 * SectionPreview — 2D control surface section preview with interactive sliders.
 *
 * Calls POST /geometry/cs-preview on slider change (debounced 50ms),
 * renders the resulting contours with react-plotly.js.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Flex, Slider, Spinner, Text } from "@radix-ui/themes";
import Plot from "react-plotly.js";
import type { Assembly, CSPreviewParams, CSPreviewResult } from "../api/geometry";
import * as geometryApi from "../api/geometry";

interface SectionPreviewProps {
  assembly: Assembly;
  surfaceName: string;
  initialParams?: Partial<CSPreviewParams>;
  onParamsChange?: (params: CSPreviewParams) => void;
}

interface SliderDef {
  key: keyof CSPreviewParams;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
}

const SLIDER_DEFS: SliderDef[] = [
  { key: "eta1", label: "\u03B71 inboard", min: 0.1, max: 0.8, step: 0.05, defaultValue: 0.55 },
  { key: "eta2", label: "\u03B72 outboard", min: 0.3, max: 1.0, step: 0.05, defaultValue: 0.95 },
  { key: "xc1", label: "x/c\u2081 hinge (inboard)", min: 0.4, max: 0.9, step: 0.01, defaultValue: 0.70 },
  { key: "xc2", label: "x/c\u2082 hinge (outboard)", min: 0.4, max: 0.9, step: 0.01, defaultValue: 0.70 },
  { key: "yh1", label: "z/h\u2081 (0=lower, 1=upper)", min: 0, max: 1, step: 0.05, defaultValue: 0.3 },
  { key: "yh2", label: "z/h\u2082", min: 0, max: 1, step: 0.05, defaultValue: 0.3 },
  { key: "gap_mm", label: "Gap (mm)", min: 0, max: 25, step: 1, defaultValue: 10 },
  { key: "delta_deg", label: "\u03B4 deflection (\u00B0)", min: -25, max: 30, step: 1, defaultValue: 15 },
  { key: "fillet_mm", label: "Cove fillet R (mm)", min: 0, max: 15, step: 1, defaultValue: 0 },
  { key: "section_eta", label: "Section \u03B7 (0=in, 1=out)", min: 0, max: 1, step: 0.05, defaultValue: 0.5 },
];

function buildDefaultParams(
  assemblyId: string,
  surfaceName: string,
  overrides?: Partial<CSPreviewParams>,
): CSPreviewParams {
  const defaults: CSPreviewParams = {
    assembly_id: assemblyId,
    surface_name: surfaceName,
    eta1: 0.55,
    eta2: 0.95,
    xc1: 0.70,
    xc2: 0.70,
    yh1: 0.3,
    yh2: 0.3,
    gap_mm: 10,
    delta_deg: 15,
    section_eta: 0.5,
    fillet_mm: 0,
  };
  return { ...defaults, ...overrides };
}

export function SectionPreview({
  assembly,
  surfaceName,
  initialParams,
  onParamsChange,
}: SectionPreviewProps) {
  const [params, setParams] = useState<CSPreviewParams>(() =>
    buildDefaultParams(assembly.id, surfaceName, initialParams),
  );
  const [preview, setPreview] = useState<CSPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionsNotReady, setSectionsNotReady] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset params when surface changes
  useEffect(() => {
    setParams(buildDefaultParams(assembly.id, surfaceName, initialParams));
  }, [assembly.id, surfaceName, initialParams]);

  // Debounced fetch
  const fetchPreview = useCallback(
    (p: CSPreviewParams) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        setLoading(true);
        setError(null);
        try {
          const result = await geometryApi.csPreview(p);
          setPreview(result);
          setSectionsNotReady(false);
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          if (msg.includes("409") || msg.includes("not yet extracted")) {
            setSectionsNotReady(true);
          } else {
            setError(msg);
          }
        } finally {
          setLoading(false);
        }
      }, 50);
    },
    [],
  );

  // Fetch on param change
  useEffect(() => {
    onParamsChange?.(params);
    fetchPreview(params);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [params, fetchPreview, onParamsChange]);

  // Retry for section cache generation
  useEffect(() => {
    if (!sectionsNotReady) return;
    const interval = setInterval(() => fetchPreview(params), 5000);
    return () => clearInterval(interval);
  }, [sectionsNotReady, params, fetchPreview]);

  const handleSlider = useCallback(
    (key: keyof CSPreviewParams, value: number) => {
      setParams((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Build Plotly traces
  const plotData = useMemo(() => {
    if (!preview) return [];
    const traces: Plotly.Data[] = [];

    // Full airfoil (faded)
    traces.push({
      x: preview.upper.map((p) => p[0] as number),
      y: preview.upper.map((p) => p[1] as number),
      mode: "lines",
      line: { color: "rgba(200,200,200,0.4)", width: 1 },
      name: "Airfoil",
      showlegend: false,
    });
    traces.push({
      x: preview.lower.map((p) => p[0] as number),
      y: preview.lower.map((p) => p[1] as number),
      mode: "lines",
      line: { color: "rgba(200,200,200,0.4)", width: 1 },
      showlegend: false,
    });

    // Wing + cove contour
    traces.push({
      x: preview.wing_contour.x,
      y: preview.wing_contour.z,
      mode: "lines",
      line: { color: "#546E7A", width: 2 },
      name: "Wing + cove",
    });

    // Cove arc (dotted)
    traces.push({
      x: preview.cove_x,
      y: preview.cove_z,
      mode: "lines",
      line: { color: "#90A4AE", width: 1, dash: "dot" },
      name: "Cove arc",
    });

    // CS at 0deg (faded)
    traces.push({
      x: preview.cs_contour_0.x,
      y: preview.cs_contour_0.z,
      mode: "lines",
      line: { color: "rgba(233,30,99,0.25)", width: 1, dash: "dot" },
      name: "CS @ 0\u00B0",
    });

    // CS deflected
    traces.push({
      x: preview.cs_contour_defl.x,
      y: preview.cs_contour_defl.z,
      mode: "lines",
      line: { color: "#E65100", width: 2 },
      name: `CS @ ${params.delta_deg}\u00B0`,
    });

    // Hinge point
    traces.push({
      x: [preview.hinge_x],
      y: [preview.hinge_z],
      mode: "markers",
      marker: { symbol: "x", size: 10, color: "#E65100" },
      name: "Hinge",
    });

    // Split points
    traces.push({
      x: [preview.su.x, preview.sl.x],
      y: [preview.su.z, preview.sl.z],
      mode: "markers",
      marker: { symbol: "diamond", size: 7, color: "#1565C0" },
      name: "Split points",
    });

    return traces;
  }, [preview, params.delta_deg]);

  const plotLayout = useMemo(
    (): Partial<Plotly.Layout> => ({
      margin: { t: 10, b: 30, l: 40, r: 10 },
      xaxis: { title: { text: "x (ft)" }, scaleanchor: "y", scaleratio: 1 },
      yaxis: { title: { text: "z (ft)" } },
      showlegend: true,
      legend: { orientation: "h", y: -0.15, font: { size: 10 } },
      uirevision: "keep",
      height: 400,
    }),
    [],
  );

  if (sectionsNotReady) {
    return (
      <Card size="2">
        <Flex align="center" gap="3" p="4" justify="center">
          <Spinner size="3" />
          <Text size="2" color="gray">
            Extracting airfoil sections (~30s)...
          </Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Flex gap="3">
      {/* Slider panel */}
      <Box style={{ width: 260, flexShrink: 0, overflowY: "auto", maxHeight: "70vh" }}>
        <Text size="2" weight="bold" mb="2" style={{ display: "block" }}>
          CS Shape Parameters
        </Text>
        {SLIDER_DEFS.map((s) => (
          <Box key={s.key} mb="2">
            <Flex justify="between" mb="1">
              <Text size="1" color="gray">{s.label}</Text>
              <Text size="1" weight="medium">
                {(params[s.key] as number).toFixed(s.step < 1 ? 2 : 0)}
              </Text>
            </Flex>
            <Slider
              min={s.min}
              max={s.max}
              step={s.step}
              value={[params[s.key] as number]}
              onValueChange={([v]) => handleSlider(s.key, v ?? s.defaultValue)}
            />
          </Box>
        ))}

        {/* Info bar */}
        {preview && (
          <Card size="1" mt="2" style={{ background: "var(--gray-2)" }}>
            <Text size="1" style={{ fontFamily: "monospace", whiteSpace: "pre-line" }}>
              {`chord: ${preview.chord.toFixed(3)} ft
R_up: ${preview.su.radius.toFixed(4)} ft
R_lo: ${preview.sl.radius.toFixed(4)} ft
ratio: ${(preview.su.radius / preview.sl.radius).toFixed(2)}`}
            </Text>
          </Card>
        )}
      </Box>

      {/* Plot */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        {error && (
          <Card size="1" mb="2" style={{ background: "var(--red-2)" }}>
            <Text size="2" color="red">{error}</Text>
          </Card>
        )}
        {loading && !preview && (
          <Flex justify="center" p="6">
            <Spinner size="3" />
          </Flex>
        )}
        {preview && (
          <Plot
            data={plotData as Plotly.Data[]}
            layout={plotLayout}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: "100%", height: 400 }}
          />
        )}
      </Box>
    </Flex>
  );
}

export default SectionPreview;
